import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, MessageSquare, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function ComplaintsSafetyExplainer() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5" />
          Understanding Complaints vs Safety Reports
        </CardTitle>
        <CardDescription>
          Two separate systems for different types of user concerns
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          {/* User Complaints */}
          <Card className="border-primary">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary" />
                User Complaints
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="font-medium text-sm mb-1">What are they?</p>
                <p className="text-sm text-muted-foreground">
                  General service issues, billing problems, technical difficulties, content concerns, service quality feedback
                </p>
              </div>
              
              <div>
                <p className="font-medium text-sm mb-1">How do users submit?</p>
                <p className="text-sm text-muted-foreground">
                  Via Settings → Help & Support → Submit Complaint button
                </p>
              </div>
              
              <div>
                <p className="font-medium text-sm mb-1">What happens?</p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Stored in <code className="text-xs bg-muted px-1 py-0.5 rounded">user_complaints</code> table</li>
                  <li>Email confirmation sent automatically (via trigger)</li>
                  <li>Appears in Complaints tab with user details</li>
                  <li>24-48 hour review commitment</li>
                  <li>Status: pending → in_progress → resolved</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Safety Reports */}
          <Card className="border-destructive">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                Safety Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="font-medium text-sm mb-1">What are they?</p>
                <p className="text-sm text-muted-foreground">
                  Serious safety concerns: harmful AI advice, crisis mishandling, privacy breaches, data errors, discrimination
                </p>
              </div>
              
              <div>
                <p className="font-medium text-sm mb-1">How do users submit?</p>
                <p className="text-sm text-muted-foreground">
                  Via Report Safety Concern button (in therapy chat and other locations)
                </p>
              </div>
              
              <div>
                <p className="font-medium text-sm mb-1">What happens?</p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Stored in <code className="text-xs bg-muted px-1 py-0.5 rounded">adverse_events</code> table</li>
                  <li>Email confirmation sent automatically (via trigger)</li>
                  <li>Appears in Safety & Investigations tab</li>
                  <li>Formal investigation required per SOP</li>
                  <li>Severity-based timeframes (critical: 24hrs)</li>
                  <li>Investigation checklist completion mandatory</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>

        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Important:</strong> Email confirmations are logged in the database. To actually send emails to users, 
            you need to implement the email-sending edge function using Resend. Check the RESEND_API_KEY secret is configured 
            and create an edge function to process the audit_logs entries with 'needs_email_confirmation': true.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}